/**
 * Tax Projections Page — What-If Scenario Engine
 *
 * Allows users to:
 * - Start from actual financial data (auto-loaded)
 * - Add/remove what-if adjustments (capital purchases, expense changes, revenue changes)
 * - See real-time tax impact with progressive bracket calculations
 * - Compare baseline vs scenario side-by-side
 * - Save scenarios for later review or investor presentations
 */

import { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Cell,
} from 'recharts';
import {
  calculateTaxProjection,
  getEntityTaxBehavior,
  type TaxCalculationInput,
  type TaxCalculationResult,
  type FilingStatus,
  type EntityType,
} from '@bizsolutions/shared';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Plus,
  Trash2,
  Save,
  RotateCcw,
  TrendingUp,
  TrendingDown,
  Calculator,
  Truck,
  Building2,
  DollarSign,
  ArrowRightLeft,
  Landmark,
  Percent,
  GitCompare,
} from 'lucide-react';

// ============================================
// Types
// ============================================

interface Adjustment {
  id: string;
  type: 'capital_purchase' | 'expense_add' | 'expense_remove' | 'revenue_change';
  name: string;
  amount: number;
  /** For capital purchases — portion deductible this year (Section 179 or depreciation) */
  deductibleAmount: number;
  /** Depreciation method for capital purchases */
  depreciationMethod?: 'section_179' | 'straight_line' | 'macrs_5' | 'macrs_7';
  category?: string;
}

interface TaxSummary {
  totalRevenue: number;
  totalDeductions: number;
  totalEstimatedPaid: number;
  netProfit: number;
}

interface SavedScenario {
  id: string;
  name: string;
  description: string | null;
  taxYear: number;
  baseRevenue: number;
  baseDeductions: number;
  filingStatus: string;
  entityType: string;
  state: string | null;
  adjustments: Adjustment[];
  isBaseline: boolean;
  updatedAt: Date;
}

interface BusinessProfile {
  id: string;
  name: string;
}

// ============================================
// Constants
// ============================================

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear + i - 1);
const formatPct = (rate: number) => `${(rate * 100).toFixed(1)}%`;

const FILING_STATUSES: { value: FilingStatus; label: string }[] = [
  { value: 'single', label: 'Single' },
  { value: 'married_filing_jointly', label: 'Married Filing Jointly' },
  { value: 'married_filing_separately', label: 'Married Filing Separately' },
  { value: 'head_of_household', label: 'Head of Household' },
];

const ENTITY_TYPES: { value: EntityType; label: string }[] = [
  { value: 'sole_proprietorship', label: 'Sole Proprietorship' },
  { value: 'single_member_llc', label: 'Single-Member LLC' },
  { value: 'partnership', label: 'Partnership' },
  { value: 's_corporation', label: 'S Corporation' },
  { value: 'c_corporation', label: 'C Corporation' },
];

const ADJUSTMENT_TYPES = [
  { value: 'capital_purchase', label: 'Capital Purchase (Equipment, Vehicle, etc.)', icon: Truck },
  { value: 'expense_add', label: 'Add Expense / Deduction', icon: TrendingDown },
  { value: 'expense_remove', label: 'Remove Expense / Deduction', icon: TrendingUp },
  { value: 'revenue_change', label: 'Revenue Change', icon: DollarSign },
] as const;

const DEPRECIATION_METHODS = [
  { value: 'section_179', label: 'Section 179 (Full Deduction This Year)' },
  { value: 'straight_line', label: 'Straight-Line (5 Year)' },
  { value: 'macrs_5', label: 'MACRS 5-Year (20% Year 1)' },
  { value: 'macrs_7', label: 'MACRS 7-Year (14.29% Year 1)' },
];

function getFirstYearDeduction(amount: number, method?: string): number {
  switch (method) {
    case 'section_179': return amount; // Full deduction
    case 'straight_line': return amount / 5; // 20% per year
    case 'macrs_5': return amount * 0.20; // MACRS 5yr first year
    case 'macrs_7': return amount * 0.1429; // MACRS 7yr first year
    default: return amount;
  }
}

function generateId(): string {
  return Math.random().toString(36).substring(2, 11);
}

/**
 * Recompute a saved scenario's tax projection from its stored base + adjustments.
 * We always recompute (rather than reading the cached `results` JSON) so the comparison
 * stays consistent if the tax engine logic ever changes.
 */
function computeScenarioProjection(scenario: SavedScenario): TaxCalculationResult {
  const revenueAdjustment = (scenario.adjustments || [])
    .filter(a => a.type === 'revenue_change')
    .reduce((sum, a) => sum + a.amount, 0);

  const adjustmentDeductions = (scenario.adjustments || []).reduce((sum, adj) => {
    if (adj.type === 'capital_purchase' || adj.type === 'expense_add') {
      return sum + adj.deductibleAmount;
    }
    if (adj.type === 'expense_remove') {
      return sum - adj.deductibleAmount;
    }
    return sum;
  }, 0);

  return calculateTaxProjection({
    grossRevenue: scenario.baseRevenue + revenueAdjustment,
    totalDeductions: scenario.baseDeductions + adjustmentDeductions,
    filingStatus: scenario.filingStatus as FilingStatus,
    entityType: scenario.entityType as EntityType,
    state: scenario.state || null,
  });
}

// Color palette for distinguishing scenarios in the comparison view
const SCENARIO_COLORS = [
  '#3b82f6', // blue
  '#10b981', // emerald
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#06b6d4', // cyan
];

// ============================================
// Component
// ============================================

export function TaxProjectionsPage() {
  const queryClient = useQueryClient();

  // Active sub-tab: scenario builder vs side-by-side comparison
  const [activeTab, setActiveTab] = useState<'builder' | 'compare'>('builder');

  // Compare-tab state: which saved scenarios are selected for side-by-side
  const [compareSelection, setCompareSelection] = useState<Set<string>>(new Set());
  const [includeBaselineInCompare, setIncludeBaselineInCompare] = useState(true);

  // Filters
  const [taxYear, setTaxYear] = useState(currentYear);
  const [businessProfileId, setBusinessProfileId] = useState<string>('all');

  // Scenario state
  const [scenarioName, setScenarioName] = useState('');
  const [filingStatus, setFilingStatus] = useState<FilingStatus>('single');
  const [entityType, setEntityType] = useState<EntityType>('sole_proprietorship');
  const [stateCode, setStateCode] = useState<string>('');
  const [revenueOverride, setRevenueOverride] = useState<number | null>(null);
  const [deductionsOverride, setDeductionsOverride] = useState<number | null>(null);
  const [adjustments, setAdjustments] = useState<Adjustment[]>([]);
  const [activeScenarioId, setActiveScenarioId] = useState<string | null>(null);

  // New adjustment form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newAdjType, setNewAdjType] = useState<Adjustment['type']>('capital_purchase');
  const [newAdjName, setNewAdjName] = useState('');
  const [newAdjAmount, setNewAdjAmount] = useState('');
  const [newAdjDepMethod, setNewAdjDepMethod] = useState('section_179');

  // Load business profiles
  const { data: profilesData } = useQuery({
    queryKey: ['business-profiles'],
    queryFn: () => api.getBusinessProfiles(),
  });
  const profiles = (profilesData?.data || []) as BusinessProfile[];

  // Load actual financial data
  const { data: summaryData } = useQuery({
    queryKey: ['tax-summary', taxYear, businessProfileId],
    queryFn: async () => {
      const result = await api.getTaxSummary({
        taxYear,
        businessProfileId: businessProfileId !== 'all' ? businessProfileId : undefined,
      });
      return result.data as {
        summary: TaxSummary | null;
        taxProjection: TaxCalculationResult | null;
      };
    },
  });

  // Load saved scenarios
  const { data: scenariosData } = useQuery({
    queryKey: ['tax-scenarios', taxYear, businessProfileId],
    queryFn: async () => {
      const result = await api.listTaxScenarios({
        taxYear,
        businessProfileId: businessProfileId !== 'all' ? businessProfileId : undefined,
      });
      return (result.data || []) as SavedScenario[];
    },
  });
  const savedScenarios = scenariosData || [];

  // Mutations
  const saveMutation = useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      if (activeScenarioId) {
        return api.updateTaxScenario(activeScenarioId, data);
      }
      return api.createTaxScenario(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tax-scenarios'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteTaxScenario(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tax-scenarios'] });
      if (activeScenarioId) {
        resetScenario();
      }
    },
  });

  // Actual data from DB
  const actualRevenue = summaryData?.summary?.totalRevenue || 0;
  const actualDeductions = summaryData?.summary?.totalDeductions || 0;
  const baselineProjection = summaryData?.taxProjection || null;

  // Effective revenue/deductions after overrides
  const effectiveRevenue = revenueOverride ?? actualRevenue;
  const baseDeductions = deductionsOverride ?? actualDeductions;

  // Calculate total adjustment impact on deductions
  const adjustmentDeductions = useMemo(() => {
    return adjustments.reduce((sum, adj) => {
      if (adj.type === 'capital_purchase' || adj.type === 'expense_add') {
        return sum + adj.deductibleAmount;
      }
      if (adj.type === 'expense_remove') {
        return sum - adj.deductibleAmount;
      }
      return sum;
    }, 0);
  }, [adjustments]);

  // Revenue adjustments
  const revenueAdjustment = useMemo(() => {
    return adjustments
      .filter(a => a.type === 'revenue_change')
      .reduce((sum, a) => sum + a.amount, 0);
  }, [adjustments]);

  const scenarioRevenue = effectiveRevenue + revenueAdjustment;
  const scenarioDeductions = baseDeductions + adjustmentDeductions;

  // Live tax calculation — runs on every change (client-side, instant)
  const scenarioProjection: TaxCalculationResult = useMemo(() => {
    const input: TaxCalculationInput = {
      grossRevenue: scenarioRevenue,
      totalDeductions: scenarioDeductions,
      filingStatus,
      entityType,
      state: stateCode || null,
    };
    return calculateTaxProjection(input);
  }, [scenarioRevenue, scenarioDeductions, filingStatus, entityType, stateCode]);

  // Comparison data
  const comparisonData = useMemo(() => {
    const baseline = baselineProjection || calculateTaxProjection({
      grossRevenue: actualRevenue,
      totalDeductions: actualDeductions,
      filingStatus,
      entityType,
      state: stateCode || null,
    });
    return [
      {
        name: 'Revenue',
        baseline: baseline.grossRevenue,
        scenario: scenarioProjection.grossRevenue,
        fill: '#3b82f6',
      },
      {
        name: 'Deductions',
        baseline: baseline.totalDeductions,
        scenario: scenarioProjection.totalDeductions,
        fill: '#10b981',
      },
      {
        name: 'Total Tax',
        baseline: baseline.totalTax,
        scenario: scenarioProjection.totalTax,
        fill: '#ef4444',
      },
      {
        name: 'Net Income',
        baseline: baseline.netIncomeAfterTax,
        scenario: scenarioProjection.netIncomeAfterTax,
        fill: '#059669',
      },
    ];
  }, [baselineProjection, scenarioProjection, actualRevenue, actualDeductions, filingStatus, entityType, stateCode]);

  const taxDifference = useMemo(() => {
    const baselineTax = baselineProjection?.totalTax || calculateTaxProjection({
      grossRevenue: actualRevenue,
      totalDeductions: actualDeductions,
      filingStatus,
      entityType,
      state: stateCode || null,
    }).totalTax;
    return scenarioProjection.totalTax - baselineTax;
  }, [baselineProjection, scenarioProjection, actualRevenue, actualDeductions, filingStatus, entityType, stateCode]);

  // Handlers
  const addAdjustment = useCallback(() => {
    const amount = parseFloat(newAdjAmount);
    if (!newAdjName || isNaN(amount) || amount === 0) return;

    const adj: Adjustment = {
      id: generateId(),
      type: newAdjType,
      name: newAdjName,
      amount,
      deductibleAmount: newAdjType === 'capital_purchase'
        ? getFirstYearDeduction(amount, newAdjDepMethod)
        : amount,
      depreciationMethod: newAdjType === 'capital_purchase' ? newAdjDepMethod as Adjustment['depreciationMethod'] : undefined,
    };

    setAdjustments(prev => [...prev, adj]);
    setNewAdjName('');
    setNewAdjAmount('');
    setShowAddForm(false);
  }, [newAdjType, newAdjName, newAdjAmount, newAdjDepMethod]);

  const removeAdjustment = useCallback((id: string) => {
    setAdjustments(prev => prev.filter(a => a.id !== id));
  }, []);

  const resetScenario = useCallback(() => {
    setAdjustments([]);
    setRevenueOverride(null);
    setDeductionsOverride(null);
    setScenarioName('');
    setActiveScenarioId(null);
  }, []);

  const loadScenario = useCallback((scenario: SavedScenario) => {
    setActiveScenarioId(scenario.id);
    setScenarioName(scenario.name);
    setFilingStatus(scenario.filingStatus as FilingStatus);
    setEntityType(scenario.entityType as EntityType);
    setStateCode(scenario.state || '');
    setRevenueOverride(scenario.baseRevenue);
    setDeductionsOverride(scenario.baseDeductions);
    setAdjustments(scenario.adjustments || []);
  }, []);

  const saveScenario = useCallback(() => {
    saveMutation.mutate({
      businessProfileId: businessProfileId !== 'all' ? businessProfileId : undefined,
      name: scenarioName || `Scenario ${new Date().toLocaleDateString()}`,
      taxYear,
      baseRevenue: effectiveRevenue,
      baseDeductions: baseDeductions,
      filingStatus,
      entityType,
      state: stateCode || null,
      adjustments,
      results: scenarioProjection,
    });
  }, [saveMutation, businessProfileId, scenarioName, taxYear, effectiveRevenue, baseDeductions, filingStatus, entityType, stateCode, adjustments, scenarioProjection]);

  // Compute baseline projection (used by the compare tab)
  const baselineCompareProjection = useMemo<TaxCalculationResult>(() => {
    return baselineProjection || calculateTaxProjection({
      grossRevenue: actualRevenue,
      totalDeductions: actualDeductions,
      filingStatus,
      entityType,
      state: stateCode || null,
    });
  }, [baselineProjection, actualRevenue, actualDeductions, filingStatus, entityType, stateCode]);

  // Comparison data: { scenario, projection } for each selected saved scenario
  const compareScenarios = useMemo(() => {
    return savedScenarios
      .filter(s => compareSelection.has(s.id))
      .map(s => ({ scenario: s, projection: computeScenarioProjection(s) }));
  }, [savedScenarios, compareSelection]);

  const toggleCompareSelection = useCallback((id: string) => {
    setCompareSelection(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Tax Projections</h1>
          <p className="text-muted-foreground">Model what-if scenarios to optimize your tax strategy</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={businessProfileId} onValueChange={setBusinessProfileId}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All Businesses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Businesses</SelectItem>
              {profiles.map(p => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={taxYear.toString()} onValueChange={(v) => setTaxYear(parseInt(v))}>
            <SelectTrigger className="w-[120px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {yearOptions.map(y => (
                <SelectItem key={y} value={y.toString()}>TY {y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'builder' | 'compare')}>
        <TabsList>
          <TabsTrigger value="builder">
            <Calculator className="h-4 w-4 mr-2" />
            Scenario Builder
          </TabsTrigger>
          <TabsTrigger value="compare">
            <GitCompare className="h-4 w-4 mr-2" />
            Compare Scenarios
          </TabsTrigger>
        </TabsList>

        <TabsContent value="builder" className="space-y-6">

      {/* Scenario Controls & Tax Settings */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tax Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Tax Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs">Entity Type</Label>
              <Select value={entityType} onValueChange={(v) => setEntityType(v as EntityType)}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ENTITY_TYPES.map(e => (
                    <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {getEntityTaxBehavior(entityType).form} — {getEntityTaxBehavior(entityType).taxMethod === 'corporate' ? 'Flat 21% federal' : 'Individual brackets'}
              </p>
            </div>
            {getEntityTaxBehavior(entityType).taxMethod === 'pass_through' ? (
              <div>
                <Label className="text-xs">Filing Status</Label>
                <Select value={filingStatus} onValueChange={(v) => setFilingStatus(v as FilingStatus)}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FILING_STATUSES.map(s => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="rounded-md bg-muted/50 p-2">
                <p className="text-xs text-muted-foreground">Filing status does not apply to C-Corporations. The entity files Form 1120 at a flat 21% federal rate.</p>
              </div>
            )}
            <div>
              <Label className="text-xs">State (2-letter code)</Label>
              <Input
                className="mt-1"
                value={stateCode}
                onChange={e => setStateCode(e.target.value.toUpperCase().slice(0, 2))}
                placeholder="e.g. TX, CA, NY"
                maxLength={2}
              />
            </div>
            {getEntityTaxBehavior(entityType).hasSelfEmploymentTax && (
              <p className="text-xs text-amber-600">SE tax applies ({entityType === 'partnership' ? 'on your K-1 share' : 'Schedule SE'})</p>
            )}
            {entityType === 's_corporation' && (
              <p className="text-xs text-blue-600">No SE tax — pay yourself a reasonable W-2 salary instead</p>
            )}
          </CardContent>
        </Card>

        {/* Base Financial Inputs */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Financial Inputs</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-xs">Gross Revenue</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="number"
                  value={revenueOverride ?? actualRevenue}
                  onChange={e => setRevenueOverride(parseFloat(e.target.value) || 0)}
                  min={0}
                  step={1000}
                />
                {revenueOverride !== null && (
                  <Button variant="ghost" size="sm" onClick={() => setRevenueOverride(null)} title="Reset to actual">
                    <RotateCcw className="h-3 w-3" />
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Actual: {formatCurrency(actualRevenue)}</p>
            </div>
            <div>
              <Label className="text-xs">Base Deductions</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="number"
                  value={deductionsOverride ?? actualDeductions}
                  onChange={e => setDeductionsOverride(parseFloat(e.target.value) || 0)}
                  min={0}
                  step={1000}
                />
                {deductionsOverride !== null && (
                  <Button variant="ghost" size="sm" onClick={() => setDeductionsOverride(null)} title="Reset to actual">
                    <RotateCcw className="h-3 w-3" />
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Actual: {formatCurrency(actualDeductions)}</p>
            </div>
          </CardContent>
        </Card>

        {/* Scenario Save/Load */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Scenarios</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs">Scenario Name</Label>
              <Input
                className="mt-1"
                value={scenarioName}
                onChange={e => setScenarioName(e.target.value)}
                placeholder="e.g. Buy Bobcat This Year"
              />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={saveScenario} disabled={saveMutation.isPending} className="flex-1">
                <Save className="h-3 w-3 mr-1" />
                {activeScenarioId ? 'Update' : 'Save'}
              </Button>
              <Button variant="outline" size="sm" onClick={resetScenario}>
                <RotateCcw className="h-3 w-3 mr-1" />
                Reset
              </Button>
            </div>
            {savedScenarios.length > 0 && (
              <div className="border-t pt-3 space-y-1 max-h-32 overflow-y-auto">
                <p className="text-xs font-medium text-muted-foreground mb-1">Saved Scenarios</p>
                {savedScenarios.map(s => (
                  <div key={s.id} className="flex items-center justify-between gap-1">
                    <Button
                      variant={activeScenarioId === s.id ? 'default' : 'ghost'}
                      size="sm"
                      className="flex-1 justify-start text-xs h-7 truncate"
                      onClick={() => loadScenario(s)}
                    >
                      {s.name}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                      onClick={() => deleteMutation.mutate(s.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* What-If Adjustments */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>What-If Adjustments</CardTitle>
          <Button size="sm" variant="outline" onClick={() => setShowAddForm(!showAddForm)}>
            <Plus className="h-4 w-4 mr-1" />
            Add Adjustment
          </Button>
        </CardHeader>
        <CardContent>
          {/* Add form */}
          {showAddForm && (
            <div className="border rounded-lg p-4 mb-4 space-y-3 bg-muted/30">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={newAdjType} onValueChange={(v) => setNewAdjType(v as Adjustment['type'])}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ADJUSTMENT_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Description</Label>
                  <Input
                    className="mt-1"
                    value={newAdjName}
                    onChange={e => setNewAdjName(e.target.value)}
                    placeholder="e.g. Bobcat S570"
                  />
                </div>
                <div>
                  <Label className="text-xs">Amount ($)</Label>
                  <Input
                    className="mt-1"
                    type="number"
                    value={newAdjAmount}
                    onChange={e => setNewAdjAmount(e.target.value)}
                    placeholder="65000"
                    min={0}
                    step={100}
                  />
                </div>
                {newAdjType === 'capital_purchase' && (
                  <div>
                    <Label className="text-xs">Depreciation Method</Label>
                    <Select value={newAdjDepMethod} onValueChange={setNewAdjDepMethod}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DEPRECIATION_METHODS.map(d => (
                          <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
              {newAdjType === 'capital_purchase' && newAdjAmount && (
                <p className="text-xs text-muted-foreground">
                  Year 1 deduction: {formatCurrency(getFirstYearDeduction(parseFloat(newAdjAmount) || 0, newAdjDepMethod))}
                </p>
              )}
              <div className="flex gap-2">
                <Button size="sm" onClick={addAdjustment}>Add</Button>
                <Button size="sm" variant="ghost" onClick={() => setShowAddForm(false)}>Cancel</Button>
              </div>
            </div>
          )}

          {/* Adjustments list */}
          {adjustments.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <ArrowRightLeft className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No adjustments yet</p>
              <p className="text-xs mt-1">Add a capital purchase, expense change, or revenue adjustment to see the tax impact</p>
            </div>
          ) : (
            <div className="space-y-2">
              {adjustments.map(adj => {
                const TypeIcon = ADJUSTMENT_TYPES.find(t => t.value === adj.type)?.icon || Calculator;
                const isPositive = adj.type === 'revenue_change' ? adj.amount > 0 : false;
                const isDeduction = adj.type === 'capital_purchase' || adj.type === 'expense_add';
                return (
                  <div key={adj.id} className="flex items-center justify-between p-3 rounded-lg border">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded ${isDeduction ? 'bg-emerald-100 text-emerald-700' : isPositive ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
                        <TypeIcon className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="text-sm font-medium">{adj.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {adj.type === 'capital_purchase'
                            ? `Cost: ${formatCurrency(adj.amount)} | Year 1 deduction: ${formatCurrency(adj.deductibleAmount)}`
                            : adj.type === 'revenue_change'
                            ? `Revenue ${adj.amount >= 0 ? 'increase' : 'decrease'}`
                            : `${adj.type === 'expense_add' ? 'Additional' : 'Removed'} deduction`
                          }
                          {adj.depreciationMethod && ` | ${DEPRECIATION_METHODS.find(d => d.value === adj.depreciationMethod)?.label}`}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <div className={`text-sm font-bold ${isDeduction ? 'text-emerald-600' : ''}`}>
                          {adj.type === 'revenue_change' ? (adj.amount >= 0 ? '+' : '') : ''}
                          {formatCurrency(adj.type === 'capital_purchase' ? adj.deductibleAmount : Math.abs(adj.amount))}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {isDeduction ? 'deduction' : adj.type === 'revenue_change' ? 'revenue' : 'removed'}
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => removeAdjustment(adj.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results: Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Scenario Revenue</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold">{formatCurrency(scenarioProjection.grossRevenue)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Total Deductions</CardTitle>
            <TrendingDown className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-emerald-600">{formatCurrency(scenarioProjection.totalDeductions)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Total Tax</CardTitle>
            <Landmark className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-destructive">{formatCurrency(scenarioProjection.totalTax)}</div>
            {taxDifference !== 0 && (
              <p className={`text-xs font-medium ${taxDifference < 0 ? 'text-emerald-600' : 'text-destructive'}`}>
                {taxDifference < 0 ? '' : '+'}{formatCurrency(taxDifference)} vs baseline
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Effective Rate</CardTitle>
            <Percent className="h-4 w-4 text-amber-600" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-amber-600">{formatPct(scenarioProjection.totalEffectiveRate)}</div>
            <p className="text-xs text-muted-foreground">Marginal: {formatPct(scenarioProjection.federalMarginalRate)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-xs font-medium">Net After Tax</CardTitle>
            <Building2 className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-emerald-600">{formatCurrency(scenarioProjection.netIncomeAfterTax)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Baseline vs Scenario Comparison */}
        <Card>
          <CardHeader>
            <CardTitle>Baseline vs Scenario Comparison</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={comparisonData}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis dataKey="name" className="text-xs" />
                <YAxis tickFormatter={(v) => `$${(Math.abs(v) / 1000).toFixed(0)}k`} className="text-xs" />
                <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                <Legend />
                <Bar dataKey="baseline" name="Baseline (Actual)" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                <Bar dataKey="scenario" name="Scenario" radius={[4, 4, 0, 0]}>
                  {comparisonData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Tax Breakdown Table */}
        <Card>
          <CardHeader>
            <CardTitle>Scenario Tax Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1">
              <div className="grid grid-cols-3 gap-2 text-xs font-medium text-muted-foreground pb-2 border-b">
                <div>Component</div>
                <div className="text-right">Amount</div>
                <div className="text-right">Eff. Rate</div>
              </div>

              <div className="grid grid-cols-3 gap-2 text-sm py-2 border-b border-dashed">
                <div>Federal Income Tax</div>
                <div className="text-right font-mono">{formatCurrency(scenarioProjection.federalTax)}</div>
                <div className="text-right font-mono">{formatPct(scenarioProjection.federalEffectiveRate)}</div>
              </div>

              <div className="grid grid-cols-3 gap-2 text-sm py-2 border-b border-dashed">
                <div>Self-Employment Tax</div>
                <div className="text-right font-mono">{formatCurrency(scenarioProjection.selfEmploymentTax)}</div>
                <div className="text-right font-mono">{formatPct(scenarioProjection.selfEmploymentEffectiveRate)}</div>
              </div>

              {scenarioProjection.stateTax > 0 && (
                <div className="grid grid-cols-3 gap-2 text-sm py-2 border-b border-dashed">
                  <div>State Tax ({stateCode || '—'})</div>
                  <div className="text-right font-mono">{formatCurrency(scenarioProjection.stateTax)}</div>
                  <div className="text-right font-mono">{formatPct(scenarioProjection.stateEffectiveRate)}</div>
                </div>
              )}

              <div className="grid grid-cols-3 gap-2 text-sm font-bold py-2 border-t">
                <div>Total</div>
                <div className="text-right font-mono">{formatCurrency(scenarioProjection.totalTax)}</div>
                <div className="text-right font-mono">{formatPct(scenarioProjection.totalEffectiveRate)}</div>
              </div>

              <div className="border-t pt-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Quarterly estimate</span>
                  <span className="font-medium">{formatCurrency(scenarioProjection.quarterlyEstimate)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Monthly set-aside</span>
                  <span className="font-medium">{formatCurrency(scenarioProjection.monthlySetAside)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Federal Bracket Detail */}
      {scenarioProjection.federalBracketBreakdown.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Federal Tax Bracket Detail</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <div className="grid grid-cols-4 gap-4 text-xs font-medium text-muted-foreground pb-2 border-b min-w-[400px]">
                <div>Bracket</div>
                <div className="text-right">Rate</div>
                <div className="text-right">Taxable Amount</div>
                <div className="text-right">Tax</div>
              </div>
              {scenarioProjection.federalBracketBreakdown.map((b) => (
                <div key={b.bracket} className="grid grid-cols-4 gap-4 text-sm py-2 border-b border-dashed last:border-0 min-w-[400px]">
                  <div className="truncate text-xs" title={b.bracket}>{b.bracket}</div>
                  <div className="text-right font-mono">{formatPct(b.rate)}</div>
                  <div className="text-right font-mono">{formatCurrency(b.taxableInThisBracket)}</div>
                  <div className="text-right font-mono">{formatCurrency(b.taxForThisBracket)}</div>
                </div>
              ))}
              <div className="grid grid-cols-4 gap-4 text-sm font-bold pt-2 border-t min-w-[400px]">
                <div>Total</div>
                <div className="text-right">{formatPct(scenarioProjection.federalEffectiveRate)}</div>
                <div className="text-right">{formatCurrency(scenarioProjection.taxableIncome)}</div>
                <div className="text-right">{formatCurrency(scenarioProjection.federalTax)}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
        </TabsContent>

        {/* ============================================================
            COMPARE SCENARIOS TAB
            ============================================================ */}
        <TabsContent value="compare" className="space-y-6">
          {/* Scenario picker */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Select Scenarios to Compare</CardTitle>
                <p className="text-xs text-muted-foreground mt-1">
                  Pick two or more saved scenarios to see them side by side. Baseline (your actual financials) is included automatically.
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={includeBaselineInCompare}
                  onChange={(e) => setIncludeBaselineInCompare(e.target.checked)}
                  className="h-4 w-4"
                />
                Include baseline
              </label>
            </CardHeader>
            <CardContent>
              {savedScenarios.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <GitCompare className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No saved scenarios yet</p>
                  <p className="text-xs mt-1">Build and save scenarios in the "Scenario Builder" tab to compare them here.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {savedScenarios.map((s) => {
                    const isSelected = compareSelection.has(s.id);
                    const colorIdx = Array.from(compareSelection).indexOf(s.id);
                    return (
                      <label
                        key={s.id}
                        className={`flex items-start gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${
                          isSelected ? 'border-primary bg-primary/5' : 'hover:bg-muted/50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleCompareSelection(s.id)}
                          className="mt-1 h-4 w-4"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {isSelected && (
                              <span
                                className="inline-block h-3 w-3 rounded-full flex-shrink-0"
                                style={{ background: SCENARIO_COLORS[colorIdx % SCENARIO_COLORS.length] }}
                              />
                            )}
                            <span className="text-sm font-medium truncate">{s.name}</span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            TY {s.taxYear} · {(s.adjustments || []).length} adjustment{(s.adjustments || []).length === 1 ? '' : 's'}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
              {(compareScenarios.length > 0 || (includeBaselineInCompare && compareScenarios.length === 0)) && (
                <div className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{compareScenarios.length} scenario{compareScenarios.length === 1 ? '' : 's'} selected</span>
                  {compareSelection.size > 0 && (
                    <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => setCompareSelection(new Set())}>
                      Clear selection
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Side-by-side metric table */}
          {(compareScenarios.length > 0 || includeBaselineInCompare) && (
            <Card>
              <CardHeader>
                <CardTitle>Side-by-Side Metrics</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 pr-4 font-medium text-xs text-muted-foreground">Metric</th>
                        {includeBaselineInCompare && (
                          <th className="text-right py-2 px-4 font-medium text-xs">
                            <div className="flex items-center justify-end gap-1.5">
                              <span className="inline-block h-2.5 w-2.5 rounded-full bg-slate-400" />
                              Baseline
                            </div>
                          </th>
                        )}
                        {compareScenarios.map(({ scenario }, i) => (
                          <th key={scenario.id} className="text-right py-2 px-4 font-medium text-xs">
                            <div className="flex items-center justify-end gap-1.5">
                              <span
                                className="inline-block h-2.5 w-2.5 rounded-full"
                                style={{ background: SCENARIO_COLORS[i % SCENARIO_COLORS.length] }}
                              />
                              <span className="truncate max-w-[140px]" title={scenario.name}>{scenario.name}</span>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(
                        [
                          { key: 'grossRevenue', label: 'Gross Revenue', format: 'currency' },
                          { key: 'totalDeductions', label: 'Total Deductions', format: 'currency', positive: true },
                          { key: 'taxableIncome', label: 'Taxable Income', format: 'currency' },
                          { key: 'federalTax', label: 'Federal Tax', format: 'currency', negative: true },
                          { key: 'selfEmploymentTax', label: 'Self-Employment Tax', format: 'currency', negative: true },
                          { key: 'stateTax', label: 'State Tax', format: 'currency', negative: true },
                          { key: 'totalTax', label: 'Total Tax', format: 'currency', negative: true, bold: true },
                          { key: 'totalEffectiveRate', label: 'Effective Rate', format: 'percent' },
                          { key: 'federalMarginalRate', label: 'Federal Marginal Rate', format: 'percent' },
                          { key: 'netIncomeAfterTax', label: 'Net After Tax', format: 'currency', positive: true, bold: true },
                          { key: 'quarterlyEstimate', label: 'Quarterly Estimate', format: 'currency' },
                          { key: 'monthlySetAside', label: 'Monthly Set-Aside', format: 'currency' },
                        ] as Array<{
                          key: keyof TaxCalculationResult;
                          label: string;
                          format: 'currency' | 'percent';
                          positive?: boolean;
                          negative?: boolean;
                          bold?: boolean;
                        }>
                      ).map(row => {
                        const fmt = (v: number) => row.format === 'currency' ? formatCurrency(v) : formatPct(v);
                        const baselineValue = baselineCompareProjection[row.key as keyof TaxCalculationResult] as number;
                        return (
                          <tr key={row.key} className="border-b border-dashed last:border-0">
                            <td className={`py-2 pr-4 ${row.bold ? 'font-bold' : ''}`}>{row.label}</td>
                            {includeBaselineInCompare && (
                              <td className={`text-right py-2 px-4 font-mono tabular-nums ${row.bold ? 'font-bold' : ''}`}>
                                {fmt(baselineValue)}
                              </td>
                            )}
                            {compareScenarios.map(({ scenario, projection }) => {
                              const value = projection[row.key as keyof TaxCalculationResult] as number;
                              const delta = value - baselineValue;
                              const deltaIsGood = row.negative ? delta < 0 : row.positive ? delta > 0 : false;
                              const deltaColor = delta === 0 ? 'text-muted-foreground' : deltaIsGood ? 'text-emerald-600' : 'text-destructive';
                              return (
                                <td key={scenario.id} className={`text-right py-2 px-4 font-mono tabular-nums ${row.bold ? 'font-bold' : ''}`}>
                                  <div>{fmt(value)}</div>
                                  {includeBaselineInCompare && delta !== 0 && (
                                    <div className={`text-[10px] font-normal ${deltaColor}`}>
                                      {delta > 0 ? '+' : ''}{fmt(delta)}
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Comparison chart */}
          {(compareScenarios.length > 0 || includeBaselineInCompare) && (
            <Card>
              <CardHeader>
                <CardTitle>Visual Comparison</CardTitle>
                <p className="text-xs text-muted-foreground">Revenue, Deductions, Total Tax, and Net After Tax across each scenario</p>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={[
                      { metric: 'Revenue', key: 'grossRevenue' as const },
                      { metric: 'Deductions', key: 'totalDeductions' as const },
                      { metric: 'Total Tax', key: 'totalTax' as const },
                      { metric: 'Net After Tax', key: 'netIncomeAfterTax' as const },
                    ].map(row => {
                      const datum: Record<string, string | number> = { metric: row.metric };
                      if (includeBaselineInCompare) {
                        datum.Baseline = baselineCompareProjection[row.key];
                      }
                      compareScenarios.forEach(({ scenario, projection }) => {
                        datum[scenario.name] = projection[row.key];
                      });
                      return datum;
                    })}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="metric" className="text-xs" />
                    <YAxis tickFormatter={(v) => `$${(Math.abs(v) / 1000).toFixed(0)}k`} className="text-xs" />
                    <Tooltip formatter={(value) => formatCurrency(Number(value))} />
                    <Legend wrapperStyle={{ fontSize: '12px' }} />
                    {includeBaselineInCompare && (
                      <Bar dataKey="Baseline" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                    )}
                    {compareScenarios.map(({ scenario }, i) => (
                      <Bar
                        key={scenario.id}
                        dataKey={scenario.name}
                        fill={SCENARIO_COLORS[i % SCENARIO_COLORS.length]}
                        radius={[4, 4, 0, 0]}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Adjustments per scenario */}
          {compareScenarios.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Adjustments per Scenario</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {compareScenarios.map(({ scenario }, i) => (
                    <div key={scenario.id} className="border rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span
                          className="inline-block h-3 w-3 rounded-full flex-shrink-0"
                          style={{ background: SCENARIO_COLORS[i % SCENARIO_COLORS.length] }}
                        />
                        <span className="text-sm font-medium truncate">{scenario.name}</span>
                      </div>
                      <div className="text-xs text-muted-foreground mb-2">
                        {scenario.entityType.replace(/_/g, ' ')} · {scenario.filingStatus.replace(/_/g, ' ')}
                        {scenario.state ? ` · ${scenario.state}` : ''}
                      </div>
                      {(scenario.adjustments || []).length === 0 ? (
                        <div className="text-xs italic text-muted-foreground">No adjustments</div>
                      ) : (
                        <ul className="space-y-1">
                          {scenario.adjustments.map((adj) => (
                            <li key={adj.id} className="text-xs flex items-center justify-between gap-2">
                              <span className="truncate">{adj.name}</span>
                              <span className="font-mono text-muted-foreground flex-shrink-0">
                                {adj.type === 'revenue_change' && (adj.amount >= 0 ? '+' : '')}
                                {formatCurrency(adj.type === 'capital_purchase' ? adj.deductibleAmount : Math.abs(adj.amount))}
                              </span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
